import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { useIsMobile } from "../shared/utils";
import { TabBar } from "../shared/TabBar";
import { InspectionSheet } from "./InspectionSheet";
import { CreateInspection } from "./CreateInspection";
import { LuLock, LuPencil, LuTrash2 } from "react-icons/lu";

export function InspectionsPage({ initialInspectionId = null, onDone = null }) {
  const userRole = localStorage.getItem("sfp_role");
  const isMobile = useIsMobile();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [hovered, setHovered] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [activeInspection, setActiveInspection] = useState(null);
  const [tab, setTab] = useState("in_progress");
  // The Follow Ups tab is office-only. /follow-ups/ is IsAdminOrManagement, so
  // a field tech fetching it would 403 — and this page is shared with techs.
  const canSeeFollowUps = userRole !== "field_tech";
  const [followUpItems, setFollowUpItems] = useState([]);
  const openedFromDashboard = useRef(false);

  const loadInspections = () => {
    axios
      .get(`${API}/inspections/`)
      .then((res) => {
        setInspections(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => {
    loadInspections();
  }, []);

  // Outstanding lines from follow-ups an inspection raised. Flattened here
  // because the tab is a to-do list of items, not of inspections.
  const loadFollowUps = useCallback(() => {
    if (!canSeeFollowUps) return;
    axios
      .get(`${API}/follow-ups/?from_inspection=1`)
      .then((res) => {
        const rows = [];
        for (const fu of res.data || []) {
          for (const it of fu.items || []) {
            if (it.completed) continue;
            rows.push({
              ...it,
              site_name: fu.site_name,
              site_address: fu.site_address,
              date: fu.date,
            });
          }
        }
        setFollowUpItems(rows);
      })
      .catch(() => setFollowUpItems([]));
  }, [canSeeFollowUps]);
  useEffect(() => {
    loadFollowUps();
  }, [loadFollowUps]);

  const completeFollowUpItem = (itemId) => {
    // Ticking the last open line closes the parent follow-up server-side via
    // FollowUp.sync_status; nothing to coordinate here.
    axios
      .patch(`${API}/follow-up-items/${itemId}/`, { completed: true })
      .then(() => loadFollowUps())
      .catch(() => {});
  };

  useEffect(() => {
    if (initialInspectionId) {
      openedFromDashboard.current = true;
      setActiveInspection(initialInspectionId);
    }
  }, [initialInspectionId]);

  // A fully-worked inspection can sit at in_progress forever — Complete aborts
  // when a fail has no notes, and merely reopening one to look at it resets the
  // status. Until it is completed its defects never reach Rectifications, so
  // flag it rather than letting it fail silently.
  const hasUncompletedFails = (insp) =>
    !insp.completed_at &&
    (insp.items || []).some((it) => it.result === "fail" && !it.swapped);

  const reopenAndEdit = (insp) => {
    if (insp.status === "in_progress") {
      setActiveInspection(insp.id);
      return;
    }
    if (insp.locked) {
      alert("This inspection is locked in Records and cannot be edited. Unlock it in Records first.");
      return;
    }
    axios.patch(`${API}/inspections/${insp.id}/`, { status: "in_progress" })
      .then(() => {
        // Reopening moves it out of Completed, so follow it across rather than
        // dropping the user back on a tab the inspection has just left.
        setTab("in_progress");
        setActiveInspection(insp.id);
      })
      .catch((err) => alert(err.response?.data?.detail || "Failed to reopen inspection."));
  };

  // Which tab an inspection belongs to. Tab ids ARE the status values, so this
  // is almost an identity — except that anything unrecognised falls through to
  // In Progress rather than to no tab at all. A strict three-way equality check
  // would make a record with a blank or unexpected status vanish from the page
  // entirely; an odd record needs attention, not burial.
  const tabOf = (i) =>
    i.status === "passed" || i.status === "failed" ? i.status : "in_progress";

  // Counted with the same function that filters, so the numbers on the tabs
  // cannot drift from the rows behind them.
  const counts = inspections.reduce((acc, i) => {
    const t = tabOf(i);
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const matchesSearch = (i) =>
    (i.site_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.site_address || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.inspector_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.assigned_to_name || "").toLowerCase().includes(search.toLowerCase());

  const filtered = inspections
    .filter((i) => tabOf(i) === tab)
    .filter(matchesSearch);

  const q = search.toLowerCase();
  const visibleFollowUps = followUpItems.filter(
    (f) =>
      (f.site_name || "").toLowerCase().includes(q) ||
      (f.site_address || "").toLowerCase().includes(q) ||
      (f.asset_label || "").toLowerCase().includes(q) ||
      (f.source_reason || "").toLowerCase().includes(q) ||
      (f.description || "").toLowerCase().includes(q),
  );

  const typeLabel = (t) =>
    ({ monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" })[t] || t;
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  if (activeInspection)
    return (
      <InspectionSheet
        inspectionId={activeInspection}
        onBack={() => {
          const wasDashboard = openedFromDashboard.current;
          openedFromDashboard.current = false;
          setActiveInspection(null);
          if (wasDashboard && onDone) {
            onDone();
          } else {
            loadInspections();
          }
        }}
      />
    );
  if (showCreate)
    return (
      <CreateInspection
        onBack={() => setShowCreate(false)}
        onCreate={() => {
          setShowCreate(false);
          loadInspections();
        }}
      />
    );

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Inspections</div>
      </div>
      <TabBar
        tabs={
          canSeeFollowUps
            ? ["in_progress", "passed", "failed", "follow_ups"]
            : ["in_progress", "passed", "failed"]
        }
        tabLabels={{
          in_progress: `In Progress (${counts.in_progress || 0})`,
          passed: `Passed (${counts.passed || 0})`,
          failed: `Failed (${counts.failed || 0})`,
          // Outstanding lines, not inspections — so this count deliberately
          // does not join the other three, which still sum to the total.
          follow_ups: `Follow Ups (${followUpItems.length})`,
        }}
        activeTab={tab}
        onSelect={setTab}
      />
      <div style={styles.content}>
        <div style={styles.card}>
          <div
            style={{
              ...styles.searchBar,
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? "10px" : "12px",
            }}
          >
            <input
              style={{
                ...styles.searchInput,
                maxWidth: isMobile ? "100%" : "50%",
              }}
              placeholder="Search by site, address or technician..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {userRole !== "field_tech" && (
              <button
                style={styles.addButton}
                onClick={() => setShowCreate(true)}
              >
                + Create Inspection
              </button>
            )}
          </div>
          {tab === "follow_ups" ? (
            /* Outstanding items, not inspections — a to-do list of the loose
               ends inspections leave behind: minor extinguisher items a tech
               flagged, and assets that were not on the wall to test. */
            visibleFollowUps.length === 0 ? (
              <div style={styles.loading}>
                {search
                  ? "No follow-ups match your search."
                  : "No outstanding follow-ups."}
              </div>
            ) : (
              <>
                {visibleFollowUps.map((fu) => (
                  <div
                    key={fu.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 16px",
                      borderBottom: `1px solid ${colors.border}`,
                      backgroundColor: hovered === `fu-${fu.id}` ? "#fafafa" : "white",
                    }}
                    onMouseEnter={() => setHovered(`fu-${fu.id}`)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: "500", color: colors.text, fontSize: "14px" }}>
                        {fu.site_name}
                      </div>
                      <div style={{ fontSize: "12px", color: colors.slate, marginTop: "2px" }}>
                        {fu.asset_label || fu.description}
                      </div>
                    </div>
                    {fu.source_reason && (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "3px 10px",
                          borderRadius: "20px",
                          backgroundColor: "#f1f5f9",
                          color: colors.slate,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fu.source_reason}
                      </span>
                    )}
                    <button
                      onClick={() => completeFollowUpItem(fu.id)}
                      title="Mark this follow-up done"
                      style={{
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: `1px solid ${colors.border}`,
                        backgroundColor: "white",
                        color: colors.slate,
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Done
                    </button>
                  </div>
                ))}
                <div style={styles.count}>
                  {visibleFollowUps.length} Follow Up
                  {visibleFollowUps.length !== 1 ? "s" : ""}
                </div>
              </>
            )
          ) : loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={styles.loading}>
              {search
                ? "No inspections match your search."
                : tab === "failed"
                  ? "No failed inspections."
                  : tab === "passed"
                    ? "No passed inspections yet."
                    : "No inspections in progress. Click + Create Inspection to get started."}
            </div>
          ) : isMobile ? (
            /* Mobile card list */
            filtered.map((insp) => (
              <div
                key={insp.id}
                onClick={() => setActiveInspection(insp.id)}
                style={{
                  padding: "14px 16px",
                  borderBottom: `1px solid ${colors.border}`,
                  backgroundColor: hovered === insp.id ? "#fafafa" : "white",
                  cursor: "pointer",
                }}
                onTouchStart={() => setHovered(insp.id)}
                onTouchEnd={() => setHovered(null)}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "6px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      color: colors.text,
                      fontSize: "15px",
                      flex: 1,
                      paddingRight: "12px",
                    }}
                  >
                    {insp.site_name}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {insp.locked && <LuLock size={13} title="Locked" style={{ color: colors.amber }} />}
                    <span style={styles.statusBadge(insp.status)}>
                      {insp.status === "in_progress"
                        ? "In Progress"
                        : insp.status === "passed"
                          ? "Passed"
                          : "Failed"}
                    </span>
                    {hasUncompletedFails(insp) && (
                      <span
                        title="This inspection has failed items but was never completed — its defects will not reach Rectifications until you press Complete."
                        style={{ color: colors.amber, fontSize: "13px", cursor: "help" }}
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: "12px", color: colors.slate, fontWeight: "500", marginBottom: "2px" }}>
                  {insp.site_area_name || "Whole Site"}
                </div>
                <div style={{ fontSize: "12px", color: "#999" }}>
                  {formatDate(insp.date)} · {typeLabel(insp.inspection_type)} ·{" "}
                  {insp.inspector_name}
                </div>
              </div>
            ))
          ) : (
            /* Desktop table */
            <>
              <div
                style={{
                  ...styles.tableHeader,
                  gridTemplateColumns: "2fr 1.3fr 1.5fr 1fr 1fr 1fr 70px",
                }}
              >
                <div>Site</div>
                <div>Section</div>
                <div>Date</div>
                <div>Type</div>
                <div>Technician</div>
                <div>Status</div>
                <div></div>
              </div>
              {filtered.map((insp) => (
                <div
                  key={insp.id}
                  style={{
                    ...styles.tableRow(hovered === insp.id),
                    gridTemplateColumns: "2fr 1.3fr 1.5fr 1fr 1fr 1fr 70px",
                  }}
                  onMouseEnter={() => setHovered(insp.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setActiveInspection(insp.id)}
                >
                  <div>
                    <div style={{ fontWeight: "500", color: colors.text }}>
                      {insp.site_name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#999" }}>
                      {insp.site_address}
                    </div>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {insp.site_area_name || "Whole Site"}
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {formatDate(insp.date)}
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {typeLabel(insp.inspection_type)}
                  </div>
                  <div style={{ fontSize: "13px" }}>{insp.inspector_name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {insp.locked && <LuLock size={13} title="Locked" style={{ color: colors.amber }} />}
                    <span style={styles.statusBadge(insp.status)}>
                      {insp.status === "in_progress"
                        ? "In Progress"
                        : insp.status === "passed"
                          ? "Passed"
                          : "Failed"}
                    </span>
                    {hasUncompletedFails(insp) && (
                      <span
                        title="This inspection has failed items but was never completed — its defects will not reach Rectifications until you press Complete."
                        style={{ color: colors.amber, fontSize: "13px", cursor: "help" }}
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {userRole !== "field_tech" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          reopenAndEdit(insp);
                        }}
                        title="Edit inspection"
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "28px", height: "28px", borderRadius: "6px",
                          border: `1px solid ${colors.border}`, backgroundColor: "white",
                          color: colors.blue, cursor: "pointer",
                        }}
                      >
                        <LuPencil size={14} />
                      </button>
                    )}
                    {userRole !== "field_tech" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this inspection?")) {
                            axios
                              .delete(`${API}/inspections/${insp.id}/`)
                              .then(() => loadInspections())
                              .catch(() => alert("Delete failed"));
                          }
                        }}
                        title="Delete inspection"
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "28px", height: "28px", borderRadius: "6px",
                          border: `1px solid ${colors.border}`, backgroundColor: "white",
                          color: colors.slate, cursor: "pointer",
                        }}
                      >
                        <LuTrash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          <div style={styles.count}>
            {filtered.length} Inspection{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
